/**
 * The Progress screen's pure reading of `GET /me/progress` (contracts §6,
 * T33). What each number MEANS is the server's definition
 * (`@repo/training`, `core/model/progress.ts`); nothing here recomputes an
 * aggregate. These helpers only choose what to headline and how two numbers
 * the server sent relate (a gain, a delta, a sum over the window).
 *
 * No React, no i18n, no locale: the screen formats.
 */
import { toCalendarDay } from "@repo/client/format";
import type { ProgressOutput } from "@repo/contract/training";

export type ProgressWeek = ProgressOutput["weeks"][number];
export type ProgressLift = ProgressOutput["lifts"][number];

/** How many weeks before the current one index `i` is (the last week is the current one). */
export function weeksAgo(index: number, count: number): number {
  return Math.max(0, count - 1 - index);
}

/** A2's chart ticks, "S1".."S8": a SLIDING window, only ever under a caption naming its length. */
export function weekTicks(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `S${i + 1}`);
}

/** No ended session anywhere in the window or the month: the new-account screen. */
export function hasHistory(progress: ProgressOutput): boolean {
  return (
    progress.month.sessions > 0 ||
    progress.weeks.some((w) => w.sessionsDone > 0 || w.volumeKg > 0)
  );
}

/** The lift's estimated 1RM per week, `null` where the week has none (a gap, never a zero). */
export function liftSeries(lift: ProgressLift): (number | null)[] {
  return lift.points.map((p) => p.e1rmKg);
}

export interface LiftGain {
  /** The first estimate in the window. */
  readonly first: number;
  readonly firstIndex: number;
  /** `last - first`; 0 with a single estimate. */
  readonly gainKg: number;
  /** The last estimate in the window. */
  readonly last: number;
  readonly lastIndex: number;
}

/** First and last estimate of a lift. `null` when no week of the window has one. */
export function liftGain(lift: ProgressLift): LiftGain | null {
  const series = liftSeries(lift);
  const firstIndex = series.findIndex((v) => v !== null);
  if (firstIndex < 0) {
    return null;
  }
  let lastIndex = firstIndex;
  series.forEach((v, i) => {
    if (v !== null) {
      lastIndex = i;
    }
  });
  const first = series[firstIndex] ?? 0;
  const last = series[lastIndex] ?? 0;
  return { first, firstIndex, last, lastIndex, gainKg: last - first };
}

export interface HeroLift {
  readonly gain: LiftGain;
  readonly lift: ProgressLift;
}

/**
 * The lift with the biggest gain over the window (A2 `HeroLift`). Ties keep
 * the server's order, which is most-trained first. `null` when no lift has an
 * estimate at all.
 */
export function heroLift(lifts: readonly ProgressLift[]): HeroLift | null {
  let best: HeroLift | null = null;
  for (const lift of lifts) {
    const gain = liftGain(lift);
    if (gain !== null && (best === null || gain.gainKg > best.gain.gainKg)) {
      best = { gain, lift };
    }
  }
  return best;
}

/**
 * Gain from the lift's first estimate to week `index`, for the Lifts header.
 * `null` when that week has no estimate or is the starting point itself.
 */
export function gainAt(
  lift: ProgressLift,
  index: number
): { kg: number; pct: number | null; weeks: number } | null {
  const gain = liftGain(lift);
  const value = lift.points[index]?.e1rmKg ?? null;
  if (gain === null || value === null || index <= gain.firstIndex) {
    return null;
  }
  const kg = value - gain.first;
  return {
    kg,
    pct: gain.first > 0 ? Math.round((kg / gain.first) * 100) : null,
    weeks: index - gain.firstIndex,
  };
}

/** Week-over-week volume change in whole percent; `null` without a previous week that lifted anything. */
export function volumeDeltaPct(
  weeks: readonly ProgressWeek[],
  index: number
): number | null {
  const current = weeks[index];
  const previous = weeks[index - 1];
  if (!(current && previous) || previous.volumeKg <= 0) {
    return null;
  }
  return Math.round(
    ((current.volumeKg - previous.volumeKg) / previous.volumeKg) * 100
  );
}

/**
 * Sessions done against planned over the CLOSED weeks of the window (A2
 * `Consistency`): the current week is still being trained, so counting its
 * unfinished plan as missed would understate every Monday.
 */
export function consistencyTotals(weeks: readonly ProgressWeek[]): {
  done: number;
  planned: number;
} {
  let done = 0;
  let planned = 0;
  for (const week of weeks.slice(0, -1)) {
    done += week.sessionsDone;
    planned += week.sessionsPlanned;
  }
  return { done, planned };
}

/**
 * "Supino reto com barra" → "Supino": enough to tell four lifts apart on a
 * chip (A2 `shortName`). When two lifts would share a first word, both keep
 * their full name.
 */
export function chipNames(lifts: readonly ProgressLift[]): string[] {
  const first = lifts.map((l) => l.name.split(" ")[0] || l.name);
  return lifts.map((lift, i) => {
    const short = first[i] ?? lift.name;
    return first.filter((f) => f === short).length > 1 ? lift.name : short;
  });
}

/** Monday of the week `weeks - 1` weeks before this one, as `YYYY-MM-DD` in local time. */
export function windowStart(now: Date, weeks: number): string {
  const sinceMonday = (now.getDay() + 6) % 7;
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - sinceMonday - 7 * (weeks - 1)
  );
  return toCalendarDay(start);
}
